/**
 * Inventário modular: bolsa (capacidade), slots equipados, slots desbloqueados,
 * shards (evolução), maestria por arquétipo e baús abertos. Estado puro + helpers;
 * o Game orquestra recálculo de stats e save. Nada de array solto no Player.
 */
import { GameConfig } from "../config/GameConfig";
import { definitionById } from "./Catalog";
import { rarityIndex, minLevelForRarity, minLevelForTier, RARITIES } from "./Rarity";
import { seedInstanceCounter, createInstance } from "./ItemFactory";
import type { EquipmentInstance, EquipmentSlot, Rarity, WeaponType } from "./EquipmentTypes";

const SLOTS: EquipmentSlot[] = ["weapon", "head", "chest", "gloves", "boots", "accessory"];
// 10.9J5 — "staff" faltava aqui (só sword/axe/bow/mace): a maestria de Mago/Necromante nunca subia
// de nível — addMasteryXp("staff", ...) achava o Map sem essa chave (nunca semeada no construtor/
// load) e retornava sem fazer nada, pra sempre. GameConfig.mastery.bonuses.staff já existia
// completo (Lv5/10/15/20) — só nunca era alcançável.
const WEAPON_TYPES: WeaponType[] = ["sword", "axe", "bow", "mace", "staff", "tome", "crossbow"]; // 10.9L25

export interface MasteryState {
  xp: number;
  level: number;
}

/** Progresso por DEFINIÇÃO de arma (Fase 10 §15): nível + cópias (sobra). */
export interface WeaponProgress {
  level: number;
  copies: number;
}

/** Resultado da auto-coleta (10.4S): item novo na bolsa, cópia de arma, ou shards. */
export type CollectResult =
  | { kind: "added"; inst: EquipmentInstance }
  | { kind: "weaponCopy"; defId: string }
  | { kind: "shards"; defId: string; amount: number }
  // 10.9K9 — cópia de uma arma que o jogador JÁ TEM (mesma raridade): entrou na barra de evolução
  // dela (`bar` = total agora) e deu `shards` fragmentos. O item em si não entra na bolsa.
  | { kind: "copy"; inst: EquipmentInstance; bar: number; shards: number };

/** 10.9K9 — situação da EVOLUÇÃO de uma arma (mesma arma + mesma raridade). */
export interface EvolveInfo {
  bar: number; // cópias acumuladas na barra
  need: number; // quantas a evolução pede
  next: Rarity | null; // raridade seguinte (null = já é mítica: não evolui)
  minLevel: number; // nível pra evoluir (= nível pra equipar a raridade nova)
  ready: boolean; // barra cheia E nível → evolui
  blocked: boolean; // barra cheia mas falta nível
}

export interface InventorySnapshot {
  bag: EquipmentInstance[];
  equipped: Record<EquipmentSlot, string | null>;
  unlocked: EquipmentSlot[];
  shards: Record<string, number>;
  mastery: Record<string, MasteryState>;
  openedChests: string[];
  weaponProgress?: Record<string, WeaponProgress>;
  evolve?: Record<string, number>; // 10.9K9 — barra de evolução por "defId|raridade"
}

export class Inventory {
  readonly bag: EquipmentInstance[] = [];
  readonly equipped: Record<EquipmentSlot, EquipmentInstance | null> = {
    weapon: null, head: null, chest: null, gloves: null, boots: null, accessory: null,
  };
  private readonly unlocked = new Set<EquipmentSlot>(["weapon"]);
  private readonly shards = new Map<string, number>();
  private readonly mastery = new Map<WeaponType, MasteryState>();
  private readonly openedChests = new Set<string>();
  private readonly weaponProgress = new Map<string, WeaponProgress>();
  private readonly evolveBars = new Map<string, number>(); // 10.9K9 — "defId|raridade" → cópias

  /** 10.9HA — espaços comprados (mochila maior); o Game sincroniza do GameProgress. */
  extraCapacity = 0;
  get capacity(): number { return GameConfig.equipment.capacity + this.extraCapacity; }

  constructor() {
    for (const t of WEAPON_TYPES) this.mastery.set(t, { xp: 0, level: 1 });
  }

  // ---- bolsa ----
  get bagCount(): number {
    return this.bag.length;
  }
  get isFull(): boolean {
    return this.bag.length >= this.capacity;
  }
  /** Adiciona à bolsa; false se cheia (o drop NÃO é destruído silenciosamente). */
  add(inst: EquipmentInstance): boolean {
    if (this.isFull) return false;
    this.bag.push(inst);
    return true;
  }

  /** "Poder" comparável de um item (raridade > nível > nº de rolls) p/ escolher o melhor. */
  private score(i: EquipmentInstance): number {
    return rarityIndex(i.rarity) * 1000 + i.level * 10 + i.rolls.length;
  }

  /**
   * AUTO-COLETA (10.4S): o item vai direto pra bolsa.
   * ARMA (10.9K5): cópias ficam (até fusion.keepMax por arma+raridade) — ver o bloco abaixo.
   * ARMADURA: se já existir uma da MESMA definição (bolsa ou equipada), a bolsa guarda só a MELHOR
   * e a outra vira shards (material da forja). Bolsa cheia com item novo → shards (nunca perde).
   */
  collect(inst: EquipmentInstance): CollectResult {
    // 10.9K9 — ARMA: se o jogador JÁ TEM essa arma nessa raridade, a cópia entra DENTRO dela (+1 na
    // barra de evolução) e dá os fragmentos de sempre — a forja continua recebendo. Arma nova (ou de
    // outra raridade) entra na bolsa. Bolsa cheia com arma nova: fragmento (nunca perde).
    if (inst.slot === "weapon") {
      if (this.countCopies(inst.definitionId, inst.rarity) > 0) {
        const shards = this.shardsForDuplicate(inst);
        this.addShards(inst.definitionId, shards);
        const bar = this.addEvolve(inst.definitionId, inst.rarity, 1);
        return { kind: "copy", inst, bar, shards };
      }
      if (this.bag.length < this.capacity) {
        this.bag.push(inst);
        return { kind: "added", inst };
      }
      return this.toShards(inst);
    }
    const bagIdx = this.bag.findIndex((i) => i.definitionId === inst.definitionId);
    const equippedSame = this.equipped[inst.slot]?.definitionId === inst.definitionId;

    // 10.9P — ARMA e ARMADURA tratadas igual: duplicata → shards (que alimentam a FORJA).
    // O "weapon level" por cópias foi aposentado; o poder da arma vem do nível de forja.
    if (bagIdx < 0 && !equippedSame) {
      if (this.bag.length < this.capacity) {
        this.bag.push(inst);
        return { kind: "added", inst };
      }
      return this.toShards(inst); // bolsa cheia: nunca perde
    }
    if (bagIdx >= 0) {
      const cur = this.bag[bagIdx];
      if (this.score(inst) > this.score(cur)) {
        this.bag[bagIdx] = inst; // o melhor fica; o antigo vira shards
        return this.toShards(cur);
      }
      return this.toShards(inst);
    }
    // só equipado possui: se o novo é melhor e cabe, disponibiliza p/ trocar; senão vira shards
    const eq = this.equipped[inst.slot]!;
    if (this.score(inst) > this.score(eq) && this.bag.length < this.capacity) {
      this.bag.push(inst);
      return { kind: "added", inst };
    }
    return this.toShards(inst);
  }

  /** Vira fragmento da própria arma/peça (o item não entra na bolsa). */
  toShards(inst: EquipmentInstance): CollectResult {
    const n = this.shardsForDuplicate(inst);
    this.addShards(inst.definitionId, n);
    return { kind: "shards", defId: inst.definitionId, amount: n };
  }

  // ---- 10.9K9 — cópias e EVOLUÇÃO de armas ----
  /** Instâncias da MESMA arma e MESMA raridade (bolsa + mão). A corrente é parte da bolsa. */
  countCopies(defId: string, rarity: Rarity): number {
    let n = 0;
    for (const i of this.bag) if (i.definitionId === defId && i.rarity === rarity) n++;
    const hand = this.equipped.weapon;
    if (hand && hand.definitionId === defId && hand.rarity === rarity) n++;
    return n;
  }

  private static barKey(defId: string, rarity: Rarity): string { return `${defId}|${rarity}`; }
  /** Cópias na barra de evolução desta arma+raridade. */
  evolveBar(defId: string, rarity: Rarity): number { return this.evolveBars.get(Inventory.barKey(defId, rarity)) ?? 0; }
  /** Soma cópias na barra (mítico não evolui: não acumula). Devolve o total. */
  addEvolve(defId: string, rarity: Rarity, n: number): number {
    if (rarityIndex(rarity) >= RARITIES.length - 1) return 0;
    const v = this.evolveBar(defId, rarity) + Math.max(0, Math.floor(n));
    this.evolveBars.set(Inventory.barKey(defId, rarity), v);
    return v;
  }

  /** Situação da evolução. A raridade nova precisa ser uma que o Caçador já consegue EQUIPAR
   *  (evoluir não pula o portão de nível — mesma regra do drop/equipar); sem nível, a barra espera. */
  evolveInfo(defId: string, rarity: Rarity, playerLevel: number): EvolveInfo {
    const need = GameConfig.equipment.evolution.copies;
    const bar = this.evolveBar(defId, rarity);
    const ri = rarityIndex(rarity);
    const next = ri >= 0 && ri < RARITIES.length - 1 ? RARITIES[ri + 1] : null;
    const minLevel = next ? this.minLevelToEquip({ rarity: next, definitionId: defId }) : 0;
    const full = !!next && bar >= need;
    return { bar, need, next, minLevel, ready: full && playerLevel >= minLevel, blocked: full && playerLevel < minLevel };
  }

  /**
   * EVOLUI a arma: a instância desta arma+raridade (a da MÃO, se for ela; senão a de maior nível de
   * forja) sobe pra raridade seguinte — MESMA instância (mão/corrente continuam apontando pra ela),
   * mesmo nível de forja, atributos sorteados de novo pra raridade nova. Gasta `copies` da barra
   * (o que passar fica). null = não deu (barra/nível/sem a arma).
   */
  evolve(defId: string, rarity: Rarity, playerLevel: number): EquipmentInstance | null {
    const info = this.evolveInfo(defId, rarity, playerLevel);
    if (!info.ready || !info.next) return null;
    const hand = this.equipped.weapon;
    const alvo = hand && hand.definitionId === defId && hand.rarity === rarity
      ? hand
      : this.bag.filter((i) => i.definitionId === defId && i.rarity === rarity).sort((a, b) => b.level - a.level)[0];
    if (!alvo) return null;
    const fresh = createInstance(defId, info.next, alvo.level);
    if (!fresh) return null;
    alvo.rarity = info.next;
    alvo.rolls = fresh.rolls;
    this.evolveBars.set(Inventory.barKey(defId, rarity), info.bar - info.need);
    return alvo;
  }

  /**
   * 10.9K9 — cópias IGUAIS que ficaram na bolsa pela regra antiga (10.9K5 guardava até 5): fica a
   * melhor (a da mão, ou a de maior forja) e cada uma das outras vira +1 na barra + fragmentos — o
   * mesmo que valeria se tivesse caído agora. Devolve quantas foram absorvidas.
   */
  absorbDuplicateWeapons(): number {
    const grupos = new Map<string, EquipmentInstance[]>();
    const hand = this.equipped.weapon;
    for (const i of [...this.bag, ...(hand ? [hand] : [])]) {
      if (i.slot !== "weapon") continue;
      const k = Inventory.barKey(i.definitionId, i.rarity);
      grupos.set(k, [...(grupos.get(k) ?? []), i]);
    }
    let n = 0;
    for (const g of grupos.values()) {
      if (g.length < 2) continue;
      // fica a de MAIOR forja; se a da mão estava no grupo, a que fica vai pra mão (ninguém perde forja)
      const fica = [...g].sort((a, b) => b.level - a.level || (a === hand ? -1 : b === hand ? 1 : 0))[0];
      if (hand && g.includes(hand) && fica !== hand) {
        this.bag.splice(this.bag.indexOf(fica), 1);
        this.equipped.weapon = fica;
        this.bag.push(hand); // sai logo abaixo, absorvida
      }
      for (const i of g) {
        if (i === fica) continue;
        const idx = this.bag.indexOf(i);
        if (idx < 0) continue;
        this.bag.splice(idx, 1);
        this.addShards(i.definitionId, this.shardsForDuplicate(i));
        this.addEvolve(i.definitionId, i.rarity, 1);
        n++;
      }
    }
    return n;
  }
  findAnywhere(instanceId: string): EquipmentInstance | null {
    for (const s of SLOTS) if (this.equipped[s]?.instanceId === instanceId) return this.equipped[s];
    return this.bag.find((i) => i.instanceId === instanceId) ?? null;
  }

  // ---- slots ----
  isSlotUnlocked(slot: EquipmentSlot): boolean {
    return this.unlocked.has(slot);
  }
  unlockSlot(slot: EquipmentSlot): boolean {
    if (this.unlocked.has(slot)) return false;
    this.unlocked.add(slot);
    return true;
  }

  /** Equipa um item da bolsa (troca com o atual, que volta pra bolsa).
   * 10.6V — se `playerLevel` for informado, refuta quando o item exige nível maior. */
  equip(instanceId: string, playerLevel?: number): boolean {
    const idx = this.bag.findIndex((i) => i.instanceId === instanceId);
    if (idx < 0) return false;
    const inst = this.bag[idx];
    if (!this.unlocked.has(inst.slot)) return false;
    // 10.9K — arma VIP/comprada ignora o nível mínimo (vantagem de quem paga)
    if (!inst.vip && typeof playerLevel === "number" && playerLevel < this.minLevelToEquip(inst)) return false;
    this.bag.splice(idx, 1);
    const prev = this.equipped[inst.slot];
    this.equipped[inst.slot] = inst;
    if (prev) this.bag.push(prev); // sempre há espaço: tiramos um antes
    return true;
  }

  /** 10.9AN — Nível mínimo pra EQUIPAR o item: o MAIOR entre raridade e TIER da armadura
   * (um peitoral T3 comum exige Nv35 mesmo sendo comum). Fonte única pra mão/corrente/UI. */
  minLevelToEquip(inst: { rarity: import("./EquipmentTypes").Rarity; definitionId?: string }): number {
    const byRarity = minLevelForRarity(inst.rarity);
    const def = inst.definitionId ? definitionById(inst.definitionId) : null;
    const tier = def ? (def as { tier?: number }).tier ?? 1 : 1; // 10.9FZ — arma tambem tem tier (fim de jogo)
    const need = Math.max(byRarity, minLevelForTier(tier));
    // 10.9L8 — anel de PROTEÇÃO é a chave dos territórios com perigo: equipa no nível em que eles abrem
    if (def && (def as { protects?: string }).protects) return Math.min(need, GameConfig.hazard.ringMinLevel);
    return need;
  }

  /** Checa se o Caçador pode equipar (nível mínimo por raridade E tier). VIP ignora nível. */
  canEquipLevel(inst: { rarity: import("./EquipmentTypes").Rarity; vip?: boolean; definitionId?: string }, playerLevel: number): boolean {
    return !!inst.vip || playerLevel >= this.minLevelToEquip(inst); // 10.9K — VIP ignora nível
  }

  /** 10.9AN — Ao carregar: item EQUIPADO acima do nível volta pra bolsa (se couber). Retorna quantos. */
  unequipIneligible(playerLevel: number): number {
    let n = 0;
    for (const s of SLOTS) {
      const inst = this.equipped[s];
      if (inst && !this.canEquipLevel(inst, playerLevel) && this.unequip(s)) n++;
    }
    return n;
  }

  /** Desequipa um slot (volta pra bolsa se houver espaço). */
  unequip(slot: EquipmentSlot): boolean {
    const inst = this.equipped[slot];
    if (!inst) return false;
    if (this.isFull) return false; // bolsa cheia: mantém equipado
    this.equipped[slot] = null;
    this.bag.push(inst);
    return true;
  }

  equippedList(): (EquipmentInstance | null)[] {
    return SLOTS.map((s) => this.equipped[s]);
  }

  // ---- shards / evolução ----
  getShards(defId: string): number {
    return this.shards.get(defId) ?? 0;
  }
  addShards(defId: string, n: number): void {
    this.shards.set(defId, this.getShards(defId) + Math.max(0, Math.floor(n)));
  }
  /** Shards concedidos ao converter uma cópia (por raridade). */
  shardsForDuplicate(inst: EquipmentInstance): number {
    return 2 + rarityIndex(inst.rarity) * 2; // common 2 ... mythic 12
  }
  /** Converte um item da bolsa em shards do seu tipo (consentido pela UI). */
  convertToShards(instanceId: string): number {
    const idx = this.bag.findIndex((i) => i.instanceId === instanceId);
    if (idx < 0) return 0;
    const inst = this.bag[idx];
    const n = this.shardsForDuplicate(inst);
    this.bag.splice(idx, 1);
    this.addShards(inst.definitionId, n);
    return n;
  }
  /** 10.9M — valor de VENDA do item em 🪙 (por raridade + nível de forja). */
  sellValue(inst: EquipmentInstance): number {
    const cfg = GameConfig.equipment.sell;
    const base = cfg.byRarity[inst.rarity] ?? cfg.byRarity.common;
    return Math.max(1, Math.round(base * (1 + Math.max(0, inst.level - 1) * cfg.perLevelPct)));
  }
  /** 10.9M — vende itens da BOLSA por moedas; retorna o total. Ignora ids equipados/inexistentes. */
  sell(instanceIds: readonly string[]): number {
    let total = 0;
    for (const id of instanceIds) {
      const idx = this.bag.findIndex((i) => i.instanceId === id);
      if (idx < 0) continue; // não está na bolsa (equipado/já vendido)
      total += this.sellValue(this.bag[idx]);
      this.bag.splice(idx, 1);
    }
    return total;
  }

  /**
   * 10.9J0 — BLESS: remove um item ALEATÓRIO da BOLSA (nunca equipado) e o retorna. É a
   * penalidade de morrer SEM bênção. Retorna null se não houver nada elegível a perder.
   *
   * Thiago: "as armas equipadas estão caindo quando morrem — não era pra cair". A regra "nunca
   * equipado" sempre valeu pro slot da mão (`equipped.weapon`), mas as armas da CORRENTE — as que
   * flutuam presas ao Caçador e atacam sozinhas — continuam sendo, pros dados, itens comuns da
   * bolsa (`ChainWeapons.setFromBag` só escolhe as de maior dano DENTRO dela; não existe um
   * "equipado 2/3/4/5" separado). Pro jogador elas SÃO equipamento em uso; sortear uma delas pra
   * perder era exatamente o que a mensagem "nunca equipado" prometia não fazer. `protectedIds`
   * tira as armas da corrente do sorteio, mantendo — sem elas — o mesmo comportamento de sempre.
   */
  loseRandom(protectedIds?: ReadonlySet<string>): EquipmentInstance | null {
    const pool = protectedIds && protectedIds.size > 0 ? this.bag.filter((it) => !protectedIds.has(it.instanceId)) : this.bag;
    if (pool.length === 0) return null;
    const chosen = pool[Math.floor(Math.random() * pool.length)];
    const idx = this.bag.indexOf(chosen);
    const [lost] = this.bag.splice(idx, 1);
    return lost ?? null;
  }

  /** Custo (shards) para subir do nível atual do item para o próximo (10.9P: função, cap 250). */
  upgradeCost(inst: EquipmentInstance): number {
    if (inst.level >= GameConfig.equipment.upgradeMaxLevel) return Infinity;
    return GameConfig.equipment.upgradeCostFn(inst.level);
  }
  /** 10.9BI — custo em FRAGMENTOS 🪙 da forja (nível × raridade). */
  upgradeCoins(inst: EquipmentInstance): number {
    if (inst.level >= GameConfig.equipment.upgradeMaxLevel) return Infinity;
    return GameConfig.equipment.upgradeCoinsFn(inst.level, rarityIndex(inst.rarity));
  }
  /** Tem shards suficientes (os 🪙 quem confere é o Game/tela, que conhecem o saldo). */
  canUpgrade(inst: EquipmentInstance): boolean {
    return this.getShards(inst.definitionId) >= this.upgradeCost(inst);
  }
  /** 10.9P — chance de QUEBRA (cair 1 nível) ao forjar este item (0 até o nível seguro). */
  breakChance(inst: EquipmentInstance): number {
    const c = GameConfig.equipment;
    if (inst.level < c.forgeSafeLevel) return 0;
    return Math.min(c.forgeBreakMax, (inst.level - c.forgeSafeLevel) * c.forgeBreakPerLevel);
  }
  /**
   * 10.9P — FORJA com risco. Gasta shards e rola a quebra. `protect` (elixir disponível+ligado)
   * impede a QUEDA quando quebra — retorna "saved". (10.9BH: o CHAMADOR consome 1 elixir em TODA
   * tentativa protegida com risco, não só quando salva.)
   * Sucesso → nível +1 ("up"); quebra sem proteção → nível −1 ("broke").
   */
  forge(instanceId: string, protect: boolean): { result: "up" | "saved" | "broke" | "nomat" | "max" | "none" } {
    const inst = this.findAnywhere(instanceId);
    if (!inst) return { result: "none" };
    if (inst.level >= GameConfig.equipment.upgradeMaxLevel) return { result: "max" };
    const cost = this.upgradeCost(inst);
    if (!Number.isFinite(cost) || this.getShards(inst.definitionId) < cost) return { result: "nomat" };
    this.shards.set(inst.definitionId, this.getShards(inst.definitionId) - cost);
    const broke = Math.random() < this.breakChance(inst);
    if (!broke) { inst.level += 1; return { result: "up" }; }
    if (protect) return { result: "saved" }; // não cai; o Game consome 1 elixir
    inst.level = Math.max(1, inst.level - 1);
    return { result: "broke" };
  }

  // ---- maestria ----
  masteryState(t: WeaponType): MasteryState {
    return this.mastery.get(t) ?? { xp: 0, level: 1 };
  }
  masteryLevel(t: WeaponType): number {
    return this.masteryState(t).level;
  }
  masteryXpForNext(t: WeaponType): number {
    return GameConfig.mastery.xpForLevel(this.masteryLevel(t));
  }
  /** Adiciona Mastery XP ao arquétipo; sobe de nível SEM TETO (10.9L22). */
  addMasteryXp(t: WeaponType, amount: number): void {
    const m = this.mastery.get(t);
    if (!m || amount <= 0) return;
    m.xp += amount;
    while (m.xp >= GameConfig.mastery.xpForLevel(m.level)) {
      m.xp -= GameConfig.mastery.xpForLevel(m.level);
      m.level += 1;
    }
  }

  // ---- weapon level por definição (Fase 10 §15) ----
  private wp(defId: string): WeaponProgress {
    let p = this.weaponProgress.get(defId);
    if (!p) {
      p = { level: 1, copies: 0 };
      this.weaponProgress.set(defId, p);
    }
    return p;
  }
  weaponLevel(defId: string): number {
    return this.wp(defId).level;
  }
  weaponCopies(defId: string): number {
    return this.wp(defId).copies;
  }
  copiesForNext(defId: string): number {
    const p = this.wp(defId);
    return p.level >= GameConfig.equipment.weaponLevel.maxLevel ? 0 : GameConfig.equipment.weaponLevel.copiesForLevel(p.level);
  }
  /** Adiciona uma cópia (drop/duplicata); auto level-up com sobra preservada.
   * Retorna o range de níveis subidos p/ o overlay (from<to) ou null se nada subiu. */
  addWeaponCopy(defId: string, n = 1): { from: number; to: number } | null {
    if (!Number.isFinite(n) || n < 1) return null;
    const cfg = GameConfig.equipment.weaponLevel;
    const p = this.wp(defId);
    const from = p.level;
    p.copies += Math.max(1, Math.floor(n));
    while (p.level < cfg.maxLevel) {
      const need = cfg.copiesForLevel(p.level);
      if (p.copies < need) break;
      p.copies -= need;
      p.level += 1;
    }
    if (p.level >= cfg.maxLevel) p.copies = 0;
    return p.level > from ? { from, to: p.level } : null;
  }
  /** Nº de milestones passivos já atingidos por esta arma (folded no recalc). */
  weaponMilestonesReached(defId: string): number {
    const lv = this.weaponLevel(defId);
    return GameConfig.equipment.weaponLevel.milestones.filter((m) => lv >= m.level).length;
  }

  // ---- baús ----
  isChestOpen(id: string): boolean {
    return this.openedChests.has(id);
  }
  markChestOpen(id: string): void {
    this.openedChests.add(id);
  }
  /** 10.6N — reabre TODOS os baús (usado pelo "Reiniciar missões" das Configurações). */
  resetChests(): void {
    this.openedChests.clear();
  }

  // ---- save ----
  snapshot(): InventorySnapshot {
    const equipped = {} as Record<EquipmentSlot, string | null>;
    for (const s of SLOTS) equipped[s] = this.equipped[s]?.instanceId ?? null;
    const mastery: Record<string, MasteryState> = {};
    for (const t of WEAPON_TYPES) mastery[t] = { ...this.masteryState(t) };
    // serializa TODAS as instâncias (bolsa + equipadas) — load reconstrói os equipados por id
    const all = [...this.bag, ...(SLOTS.map((s) => this.equipped[s]).filter(Boolean) as EquipmentInstance[])];
    return {
      bag: all.map((i) => ({ ...i, rolls: i.rolls.map((r) => ({ ...r })) })),
      equipped,
      unlocked: [...this.unlocked],
      shards: Object.fromEntries(this.shards),
      mastery,
      openedChests: [...this.openedChests],
      weaponProgress: Object.fromEntries([...this.weaponProgress].map(([k, v]) => [k, { ...v }])),
      evolve: Object.fromEntries(this.evolveBars), // 10.9K9
    };
  }

  load(snap: Partial<InventorySnapshot> | undefined): void {
    this.bag.length = 0;
    for (const s of SLOTS) this.equipped[s] = null;
    this.unlocked.clear();
    this.unlocked.add("weapon");
    this.shards.clear();
    this.openedChests.clear();
    this.weaponProgress.clear();
    this.evolveBars.clear(); // 10.9K9
    for (const t of WEAPON_TYPES) this.mastery.set(t, { xp: 0, level: 1 });
    if (!snap || typeof snap !== "object") return;
    // 10.9K9 — barra de evolução ("defId|raridade" → cópias); valor inválido some
    if (snap.evolve && typeof snap.evolve === "object") {
      for (const [k, v] of Object.entries(snap.evolve)) {
        const n = Math.floor(Number(v));
        if (typeof k === "string" && k.includes("|") && Number.isFinite(n) && n > 0) this.evolveBars.set(k, Math.min(n, 1_000_000));
      }
    }

    const all: EquipmentInstance[] = [];
    const sanitize = (i: EquipmentInstance): EquipmentInstance | null => {
      if (!i || typeof i !== "object") return null;
      const def = definitionById(i.definitionId);
      if (!def) return null;
      if (typeof i.instanceId !== "string" || !i.instanceId) return null;
      const rarity = ["common", "uncommon", "rare", "epic", "legendary", "mythic"].includes(i.rarity) ? i.rarity : "common";
      return {
        instanceId: String(i.instanceId),
        definitionId: i.definitionId,
        slot: def.slot,
        rarity,
        level: Math.min(GameConfig.equipment.upgradeMaxLevel, Math.max(1, Math.floor(Number.isFinite(i.level) ? i.level : 1))),
        // 10.9FR — TODAS as chaves de atributo sobrevivem (antes só 8: itens perdiam os % ao recarregar)
        rolls: Array.isArray(i.rolls) ? i.rolls.filter((r) => r && typeof r.value === "number" && Number.isFinite(r.value) && ["damage", "attackSpeed", "critChance", "critDamage", "maxHp", "armor", "moveSpeed", "attackRange", "dmgSlash", "dmgPierce", "dmgBlunt", "defSlash", "defPierce", "defBlunt", "block", "regen", "lifesteal", "maxHpPct", "damagePct"].includes(r.key)) : [],
        ...(i.vip ? { vip: true as const } : {}), // 10.9K — preserva a marca VIP (senão o load derruba, igual bug de diamantes/mail)
      };
    };
    for (const raw of Array.isArray(snap.bag) ? snap.bag : []) {
      const s = sanitize(raw);
      if (s && !all.some((i) => i.instanceId === s.instanceId)) all.push(s);
    }
    // reconstrói equipados a partir dos ids (podem estar no bag serializado)
    const equippedIds = snap.equipped ?? ({} as Record<EquipmentSlot, string | null>);
    const byId = new Map(all.map((i) => [i.instanceId, i]));
    for (const s of SLOTS) {
      const id = equippedIds[s];
      const inst = id ? byId.get(id) : undefined;
      if (inst && inst.slot === s) {
        this.equipped[s] = inst;
      }
    }
    // o que não ficou equipado vai pra bolsa (respeitando capacidade)
    const equippedSet = new Set(SLOTS.map((s) => this.equipped[s]?.instanceId).filter(Boolean) as string[]);
    for (const i of all) {
      if (equippedSet.has(i.instanceId)) continue;
      this.bag.push(i); // 10.9GM — carrega TUDO (a capacidade só limita entradas NOVAS; brindes/anexos acima do teto sumiam ao recarregar)
    }
    // seed do contador de ids p/ não colidir
    let maxN = 0;
    for (const i of all) {
      const m = /^it_(\d+)_/.exec(i.instanceId);
      if (m) maxN = Math.max(maxN, Number(m[1]));
    }
    seedInstanceCounter(maxN);

    for (const s of Array.isArray(snap.unlocked) ? snap.unlocked : ["weapon"] as EquipmentSlot[]) if (SLOTS.includes(s)) this.unlocked.add(s);
    for (const [k, v] of Object.entries(snap.shards ?? {})) {
      if (typeof v === "number" && Number.isFinite(v)) this.shards.set(k, Math.max(0, Math.min(999999, Math.floor(v))));
    }
    for (const t of WEAPON_TYPES) {
      const m = snap.mastery?.[t];
      if (m) {
        this.mastery.set(t, {
          xp: Math.max(0, Math.floor(m.xp ?? 0)),
          level: Math.max(1, Math.floor(m.level ?? 1)), // 10.9L22 — sem teto
        });
      }
    }
    for (const id of Array.isArray(snap.openedChests) ? snap.openedChests : []) if (typeof id === "string") this.openedChests.add(id);
    const maxWL = GameConfig.equipment.weaponLevel.maxLevel;
    for (const [k, v] of Object.entries(snap.weaponProgress ?? {})) {
      if (v && Number.isFinite(v.level) && Number.isFinite(v.copies)) {
        this.weaponProgress.set(k, { level: Math.min(maxWL, Math.max(1, Math.floor(v.level))), copies: Math.max(0, Math.floor(v.copies)) });
      }
    }
  }

  reset(): void {
    this.load(undefined);
  }
}
